import React, { useMemo, useState, useEffect } from 'react';
import { useProfileFormContext } from './context/ProfileFormContext';
import { useAuth } from '../../contexts/AuthContext';
import { getJewellerRings } from '../../services/product';
import SegmentControl from '../ui/1-molecules/SegmentControl/SegmentControl';
import StorefrontProfileHeader from '../ui/2-organisms/StorefrontProfileHeader/StorefrontProfileHeader';
import AboutTabContent from '../storefronts/AboutTabContent';
import RingsTabContent from '../storefronts/RingsTabContent';
import Tag from '../ui/0-atoms/Tag/Tag';
import styles from './StorefrontPreview.module.css';

const StorefrontPreview = () => {
  const { formData } = useProfileFormContext();
  const { jeweller } = useAuth();
  const [activeTab, setActiveTab] = useState('about');
  const [rings, setRings] = useState([]);
  const [ringsLoading, setRingsLoading] = useState(false);
  const [ringsError, setRingsError] = useState(null);

  useEffect(() => {
    const loadRings = async () => {
      if (!jeweller?.id) return;
      setRingsLoading(true);
      setRingsError(null);
      try {
        const result = await getJewellerRings(jeweller.id);
        setRings(Array.isArray(result) ? result : []);
      } catch (error) {
        setRingsError('Unable to load rings for preview.');
      } finally {
        setRingsLoading(false);
      }
    };
    loadRings();
  }, [jeweller?.id]);

  const previewJeweller = useMemo(() => {
    const galleryImagesSource = formData.galleryImages || formData.galleryPhotos;
    const galleryPhotos = Array.isArray(galleryImagesSource)
      ? galleryImagesSource
      : galleryImagesSource && typeof galleryImagesSource === 'object'
        ? Object.values(galleryImagesSource)
        : [];

    return {
      ...formData,
      id: jeweller?.id,
      showcaseVideo: formData.showcaseVideo || formData.videoUrl || '',
      galleryImages: galleryPhotos.filter(Boolean),
      headerImage: formData.headerImage || '',
      profilePhoto: formData.profilePhoto || '',
      companyName: formData.companyName || '',
      location: formData.location || '',
      introParagraph: formData.introParagraph || '',
      bio: formData.bio || '',
      ethicalStatement: formData.ethicalStatement || '',
      awards: Array.isArray(formData.awards) ? formData.awards : [],
    };
  }, [formData, jeweller?.id]);

  const tagMap = useMemo(() => ({
    offersWarranty: 'Offers Warranty',
    queerFriendly: 'Queer Friendly',
    freeRingResizing: 'Free Ring Resizing',
    ethicalJeweller: 'Ethical Jeweller',
    emergingDesigner: 'Emerging Designer',
    lgbtqOwned: 'LGBTQ+ Owned',
    madeuk: 'Made in the UK'
  }), []);

  const formatPrice = (price) => {
    const value = Number(price || 0);
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
      .format(value)
      .replace('.00', '');
  };

  const previewTags = useMemo(() => {
    const items = [];
    const startingPrice = formData.startingPrice;
    const timeframe = formData.minimumTimeframe;

    items.push({
      text: `Budget (from ${formatPrice(startingPrice)})`,
      variant: 'positive'
    });

    const timeframeText = timeframe ? `${timeframe} weeks` : 'Not specified';
    items.push({
      text: `Timeframe (${timeframeText})`,
      variant: 'positive'
    });

    if (formData.tags) {
      Object.entries(tagMap)
        .filter(([key]) => formData.tags?.[key])
        .forEach(([, label]) => {
          items.push({ text: label, variant: 'neutral' });
        });
    }

    return items;
  }, [formData.startingPrice, formData.minimumTimeframe, formData.tags, tagMap]);

  const tabOptions = [
    { id: 'about', label: 'About' },
    { id: 'rings', label: 'Rings' },
    { id: 'match', label: 'Match' }
  ];

  const topRings = useMemo(() => rings.slice(0, 2), [rings]);
  const handleRingClick = () => {};

  return (
    <div className={styles.previewWrapper}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>Profile Preview</h3>
          <p className={styles.subtitle}>
            This is how customers will see your storefront in the app. Note that some features are turned off in preview mode, but will work as expected on the real app.
          </p>
        </div>
      </div>

      <div className={styles.previewCard}>
        <div className={styles.phoneFrame}>
          <div className={styles.phoneNotch} />
          <div className={styles.phoneScreen}>
            <div className={styles.phoneScroll}>
              <StorefrontProfileHeader jeweller={previewJeweller} />

              {previewTags.length > 0 && (
                <div className={styles.tagRow}>
                  {previewTags.map((tag) => (
                    <Tag key={tag.text} variant={tag.variant} text={tag.text} />
                  ))}
                </div>
              )}

            <div className={styles.tabs}>
              <SegmentControl
                items={tabOptions}
                activeItemId={activeTab}
                onItemClick={setActiveTab}
              />
            </div>

            <div className={styles.tabContent}>
              {activeTab === 'about' && (
                <AboutTabContent
                  jeweller={previewJeweller}
                  topRings={topRings}
                  onRingClick={handleRingClick}
                  previewMode
                />
              )}

              {activeTab === 'rings' && (
                <>
                  {ringsLoading && (
                    <div className={styles.placeholderText}>Loading rings…</div>
                  )}
                  {ringsError && (
                    <div className={styles.placeholderText}>{ringsError}</div>
                  )}
                  {!ringsLoading && !ringsError && (
                    <RingsTabContent rings={rings} onRingClick={handleRingClick} />
                  )}
                </>
              )}

              {activeTab === 'match' && (
                <div className={styles.placeholder}>
                  <h4>Match Preview Unavailable</h4>
                  <p>
                    This section is personalised to each customer&apos;s Style Profile in the app.
                    It isn&apos;t shown in preview mode.
                  </p>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorefrontPreview;
